import { Controller } from "@hotwired/stimulus";
import WaveSurfer from "wavesurfer.js";

export default class extends Controller {
  static targets = ["audio", "time", "waveform", "progress"];
  static values = { cuePoint: Number, trackName: String };

  connect() {
    this._ws = null;
    this._ready = false;
    this._wsInited = false;
    this._playOnReady = false;
    this._onPauseAll = (e) => {
      if (e.detail.except !== this.element) this.pause();
    };
    document.addEventListener("audio-player:pause-all", this._onPauseAll);

    if (!this.hasWaveformTarget) {
      this.#initPlain(); // edit form — plain <audio>
    }
    // WaveSurfer mode: lazy init on first click
  }

  disconnect() {
    document.removeEventListener("audio-player:pause-all", this._onPauseAll);
    if (this.element.classList.contains("is-playing")) this.#announceStopped();
    if (this._ws) {
      this._ws.destroy();
      this._ws = null;
    } else if (this.hasAudioTarget) {
      this.audioTarget.removeEventListener("timeupdate", this._onTimeUpdate);
      this.audioTarget.removeEventListener("ended", this._onEnded);
    }
  }

  // ── Toggle ────────────────────────────────────────────────────────────────

  toggle(event) {
    if (event?.target?.closest(".bgm-manage-row__actions")) return;
    if (event?.target?.closest(".waveform-scrubber")) return;

    if (this.hasWaveformTarget) {
      if (!this._wsInited) {
        this.#initWaveSurfer();
        this._playOnReady = true;
        this.element.classList.add("is-loading");
        return;
      }
      if (!this._ready) return; // still loading, wait for ready event
      this._ws.isPlaying() ? this.pause() : this.#wsPlay();
    } else {
      this.audioTarget.paused ? this.#plainPlay() : this.pause();
    }
  }

  pause() {
    this._playOnReady = false; // cancels both WaveSurfer and plain pending plays
    this.element.classList.remove("is-playing", "is-loading");
    if (this._ws) {
      if (this._ready) this._ws.pause();
    } else if (this.hasAudioTarget) {
      this.audioTarget.pause();
    }
    if (this.hasWaveformTarget) this.waveformTarget.hidden = true;
    this.#announceStopped();
  }

  // ── Now-playing bar (bgm_tracks index only — no-op elsewhere) ─────────────

  #announcePlaying() {
    if (!this.hasTrackNameValue || !this.trackNameValue) return;
    document.dispatchEvent(new CustomEvent("audio-player:now-playing", {
      detail: { name: this.trackNameValue, element: this.element },
    }));
    // Same "land in a good spot" behavior as clicking the now-playing bar's
    // title (now_playing_controller#jumpToTrack) — clicking the card directly
    // to start playback should settle it the same way, not just scroll the
    // rest of the page underneath it wherever it happened to be clicked.
    this.element.scrollIntoView({ behavior: "smooth", block: "center" });
    this.element.classList.add("track-card--flash");
    setTimeout(() => this.element.classList.remove("track-card--flash"), 900);
  }

  #announceStopped() {
    if (!this.hasTrackNameValue || !this.trackNameValue) return;
    document.dispatchEvent(new CustomEvent("audio-player:stopped", {
      detail: { element: this.element },
    }));
  }

  // Plain scrubber bar seek — edit form only
  seek(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    if (this.hasAudioTarget && isFinite(this.audioTarget.duration)) {
      this.audioTarget.currentTime = ratio * this.audioTarget.duration;
    }
  }

  // ── Plain <audio> mode (edit form) ───────────────────────────────────────

  #initPlain() {
    if (!this.hasAudioTarget) return;
    this._ready = true;

    this._onTimeUpdate = () => {
      const cur = this.audioTarget.currentTime;
      const dur = this.audioTarget.duration || 0;
      if (this.hasTimeTarget) this.timeTarget.textContent = this.#fmt(cur);
      if (this.hasProgressTarget) this.progressTarget.style.width = dur > 0 ? `${(cur / dur) * 100}%` : "0%";
    };

    this._onEnded = () => {
      this.element.classList.remove("is-playing");
      if (this.hasTimeTarget) this.timeTarget.textContent = this.#fmt(0);
      if (this.hasProgressTarget) this.progressTarget.style.width = "0%";
      this.#announceStopped();
    };

    this.audioTarget.addEventListener("timeupdate", this._onTimeUpdate);
    this.audioTarget.addEventListener("ended", this._onEnded);
  }

  #plainPlay() {
    document.dispatchEvent(new CustomEvent("audio-player:pause-all", { detail: { except: this.element } }));

    const audio = this.audioTarget;
    const cue = this.cuePointValue;

    // If audio hasn't buffered enough to play yet, show loading spinner
    if (audio.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      this.element.classList.add("is-loading");

      const onCanPlay = () => {
        audio.removeEventListener("canplay", onCanPlay);
        if (!this._playOnReady) return; // pause-all fired while loading
        this.element.classList.remove("is-loading");
        if (cue > 0 && isFinite(audio.duration)) audio.currentTime = cue;
        audio.play();
        this.element.classList.add("is-playing");
        this._playOnReady = false;
        this.#announcePlaying();
      };

      const onError = () => {
        audio.removeEventListener("error", onError);
        this.element.classList.remove("is-loading");
        this._playOnReady = false;
      };

      this._playOnReady = true;
      audio.addEventListener("canplay", onCanPlay, { once: true });
      audio.addEventListener("error", onError, { once: true });
      audio.load();
      return;
    }

    if (cue > 0 && isFinite(audio.duration)) audio.currentTime = cue;
    audio.play();
    this.element.classList.add("is-playing");
    this.#announcePlaying();
  }

  // ── WaveSurfer mode (library index) ──────────────────────────────────────

  #initWaveSurfer() {
    if (!this.hasAudioTarget) return;
    this._wsInited = true;

    const src = this.audioTarget.querySelector("source")?.src || this.audioTarget.src;
    if (!src) return;

    // --border is near-white in light mode — invisible as wave bars on a
    // white surface. --white never existed as a token (always fell back to
    // the hardcoded default). Use tokens that actually contrast against
    // --surface in both themes.
    const style = getComputedStyle(document.documentElement);
    const waveColor = style.getPropertyValue("--slate").trim() || "#27272a";
    const progressColor = style.getPropertyValue("--primary").trim() || "#4F46E5";
    const cursorColor = style.getPropertyValue("--text").trim() || "#a7a7a7";

    const peaksRaw = this.waveformTarget.dataset.peaks;
    const peaks = peaksRaw ? JSON.parse(peaksRaw) : null;

    const options = {
      container: this.waveformTarget,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 2,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 48,
      normalize: true,
      interact: true,
      url: src,
    };

    if (peaks && peaks.length > 0) options.peaks = [peaks];

    this._ws = WaveSurfer.create(options);

    this.waveformTarget.addEventListener("pointerdown", (e) => e.stopPropagation());

    this._ws.on("ready", () => {
      this._ready = true;
      this.element.classList.remove("is-loading");
      if (this._playOnReady) {
        this._playOnReady = false;
        const cue = this.cuePointValue;
        if (cue > 0) this._ws.setTime(cue);
        this.#wsPlay();
      }
    });

    this._ws.on("timeupdate", (t) => {
      if (this.hasTimeTarget) this.timeTarget.textContent = this.#fmt(t);
    });

    this._ws.on("interaction", () => {
      if (!this._ws.isPlaying()) {
        document.dispatchEvent(new CustomEvent("audio-player:pause-all", { detail: { except: this.element } }));
        this.waveformTarget.hidden = false;
        this._ws.play();
        this.element.classList.add("is-playing");
        this.#announcePlaying();
      }
    });

    this._ws.on("finish", () => {
      this.element.classList.remove("is-playing");
      if (this.hasWaveformTarget) this.waveformTarget.hidden = true;
      if (this.hasTimeTarget) this.timeTarget.textContent = this.#fmt(0);
      this.#announceStopped();
    });
  }

  #wsPlay() {
    document.dispatchEvent(new CustomEvent("audio-player:pause-all", { detail: { except: this.element } }));
    if (this.hasWaveformTarget) this.waveformTarget.hidden = false;
    this._ws.play();
    this.element.classList.add("is-playing");
    this.#announcePlaying();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  #fmt(s) {
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  }
}
